import { LitElement, PropertyValues } from 'lit'
import { customElement, property } from 'lit/decorators.js'
import { createContext, provide } from '@lit/context'
import { RmaDraftDTO } from 'components/ordering/rma/api/dtos/rma-draft-dto.js'
import { RmaReasonDTO } from 'components/ordering/rma/api/dtos/rma-reason-dto.js'
import { RmaConfigDTO } from 'components/ordering/rma/api/dtos/rma-config-dto.js'
import { RmaProductDTO } from 'components/ordering/rma/api/dtos/rma-product-dto.js'
import { RmaOrderDTO } from 'components/ordering/rma/api/dtos/rma-order-dto.js'
import { executeRmaRequest, setRmaBaseUrl } from 'components/ordering/rma/api/executor.js'
import { RmaError } from 'components/ordering/rma/api/dtos/error.js'
import { GetRmaReasons } from 'components/ordering/rma/api/requests/get-rma-reasons.js'
import { GetRmaConfig } from 'components/ordering/rma/api/requests/get-rma-config.js'
import { FetchRmaDraft } from 'components/ordering/rma/api/requests/fetch-rma-draft.js'
import { CreateRmaDraft } from 'components/ordering/rma/api/requests/create-rma-draft.js'
import { DeleteRmaDraft } from 'components/ordering/rma/api/requests/delete-rma-draft.js'
import { CreateRmaLine } from 'components/ordering/rma/api/requests/create-rma-line.js'
import { UpdateRmaLine } from 'components/ordering/rma/api/requests/update-rma-line.js'
import { DeleteRmaLine } from 'components/ordering/rma/api/requests/delete-rma-line.js'
import { UpdateRmaComment } from 'components/ordering/rma/api/requests/update-rma-comment.js'
import { SubmitRmaDraft } from 'components/ordering/rma/api/requests/submit-rma-draft.js'
import { SearchRmaOrders } from 'components/ordering/rma/api/requests/search-rma-orders.js'
import { SearchRmaDeliveredProducts } from 'components/ordering/rma/api/requests/search-rma-delivered-products.js'
import { SearchRmaProducts } from 'components/ordering/rma/api/requests/search-rma-products.js'
import { AddRmaLineUpload } from 'components/ordering/rma/api/requests/add-rma-line-upload.js'
import { RemoveRmaLineUpload } from 'components/ordering/rma/api/requests/remove-rma-line-upload.js'
import { CreateTemporaryRmaLineUpload } from 'components/ordering/rma/api/requests/create-temporary-rma-line-upload.js'
import { DeleteTemporaryRmaLineUpload } from 'components/ordering/rma/api/requests/delete-temporary-rma-line-upload.js'
import { fetcher } from 'components/shared/fetcher.js'


export class NotFetchedYet {
  notFetchedYetBrand = Symbol('NOT_FETCHED_YET_BRAND')
}


export const draftRmaContext = createContext<RmaDraftDTO | null | NotFetchedYet>(Symbol('RMA_DRAFT_CONTEXT'))
export const reasonsContext = createContext<RmaReasonDTO[]>(Symbol('RMA_REASONS_CONTEXT'))
export const configContext = createContext<RmaConfigDTO | NotFetchedYet>(Symbol('RMA_CONFIG_CONTEXT'))
export const searchOrdersContext = createContext<RmaOrderDTO[]>(Symbol('RMA_SEARCH_ORDERS_CONTEXT'))
export const searchDeliveriesContext = createContext<RmaOrderDTO[]>(Symbol('RMA_SEARCH_DELIVERIES_CONTEXT'))
export const searchProductsContext = createContext<RmaProductDTO[]>(Symbol('RMA_SEARCH_PRODUCTS_CONTEXT'))
export const latestOrdersContext = createContext<RmaOrderDTO[]>(Symbol('RMA_LATEST_ORDERS_CONTEXT'))

export const deleteRmaContext = createContext<() => Promise<void>>(Symbol('RMA_DELETE_RMA_CONTEXT'))
export const createRmaLineContext = createContext<(payload: unknown) => Promise<void>>(Symbol('RMA_CREATE_LINE_CONTEXT'))
export const updateRmaLineContext = createContext<(payload: unknown) => Promise<void>>(Symbol('RMA_UPDATE_LINE_CONTEXT'))
export const deleteRmaLineContext = createContext<(lineId: string) => Promise<void>>(Symbol('RMA_DELETE_LINE_CONTEXT'))
export const updateRmaCommentContext = createContext<(comment: string | null) => Promise<void>>(Symbol('RMA_UPDATE_COMMENT_CONTEXT'))
export const enableRmaCommentContext = createContext<boolean>(Symbol('RMA_ENABLE_COMMENT_CONTEXT'))
export const submitRmaContext = createContext<(rmaId: string) => Promise<void>>(Symbol('RMA_SUBMIT_CONTEXT'))
export const fetchOrdersContext = createContext<(searchTerm: string) => Promise<void>>(Symbol('RMA_FETCH_ORDERS_CONTEXT'))
export const fetchDeliveriesContext = createContext<(searchTerm: string) => Promise<void>>(Symbol('RMA_FETCH_DELIVERIES_CONTEXT'))
export const fetchProductsOfOrderContext = createContext<(shipmentID: string, saleID: string) => Promise<RmaProductDTO[]>>(Symbol('RMA_FETCH_PRODUCTS_OF_ORDER_CONTEXT'))
export const fetchProductSearchContext = createContext<(searchTerm: string) => Promise<void>>(Symbol('RMA_FETCH_PRODUCT_SEARCH_CONTEXT'))
export const fetchLatestOrdersContext = createContext<() => Promise<void>>(Symbol('RMA_FETCH_LATEST_ORDERS_CONTEXT'))
export const uploadUrlContext = createContext<string>(Symbol('RMA_UPLOAD_URL_CONTEXT'))
// Per-shop field configuration: some shops do not collect every memo.
export const disableMemoContext = createContext<boolean>(Symbol('RMA_DISABLE_MEMO_CONTEXT'))
export const disableMemo2Context = createContext<boolean>(Symbol('RMA_DISABLE_MEMO2_CONTEXT'))
export const disableMemo3Context = createContext<boolean>(Symbol('RMA_DISABLE_MEMO3_CONTEXT'))
export const uploadRmaLineFileContext = createContext<(lineId: string, file: File) => Promise<void>>(Symbol('RMA_UPLOAD_LINE_FILE_CONTEXT'))
export const removeRmaLineUploadContext = createContext<(lineId: string, identifier: string) => Promise<void>>(Symbol('RMA_REMOVE_LINE_UPLOAD_CONTEXT'))
export const uploadTemporaryRmaLineFileContext = createContext<(file: File) => Promise<string>>(Symbol('RMA_UPLOAD_TEMPORARY_LINE_FILE_CONTEXT'))
export const removeTemporaryRmaLineUploadContext = createContext<(identifier: string) => Promise<void>>(Symbol('RMA_REMOVE_TEMPORARY_LINE_UPLOAD_CONTEXT'))


const reformatLocale = (locale: string | null): Intl.LocalesArgument => {
  if (locale === null) throw new Error('locale is required')
  const localeParts = locale.split('_')
  if (localeParts.length === 2) {
    return [localeParts?.[0]?.toLowerCase(), localeParts?.[1]?.toUpperCase()].join('-')
  }
  return locale
}


@customElement('csc-rma-context')
export class CSCRmaContext extends LitElement {
  protected override createRenderRoot = () => this

  @provide({ context: draftRmaContext })
  public draftRma: RmaDraftDTO | null | NotFetchedYet = new NotFetchedYet()

  @provide({ context: reasonsContext })
  public reasons: RmaReasonDTO[] = []

  @provide({ context: configContext })
  public config: RmaConfigDTO | NotFetchedYet = new NotFetchedYet()

  @provide({ context: searchOrdersContext })
  public searchOrders: RmaOrderDTO[] = []

  @provide({ context: searchDeliveriesContext })
  public searchDeliveries: RmaOrderDTO[] = []

  @provide({ context: searchProductsContext })
  public searchProducts: RmaProductDTO[] = []

  @provide({ context: latestOrdersContext })
  public latestOrders: RmaOrderDTO[] = []

  @property({ attribute: 'base-path', type: String })
  public basePath!: string

  @property({ attribute: 'locale', type: String, converter: reformatLocale })
  public locale!: Intl.LocalesArgument

  @provide({ context: uploadUrlContext })
  @property({ attribute: 'upload-url', type: String })
  public uploadUrl = ''

  @provide({ context: enableRmaCommentContext })
  @property({ attribute: 'enable-rma-comment', type: Boolean })
  public enableRmaComment = false

  // Driven entirely by the fetched shop config (see fetchConfig below) — not
  // frontend-settable, so a shop's field visibility can't be overridden client-side.
  @provide({ context: disableMemoContext })
  public disableMemo = false

  @provide({ context: disableMemo2Context })
  public disableMemo2 = false

  @provide({ context: disableMemo3Context })
  public disableMemo3 = false

  private requestCache: Map<string, Promise<void>> = new Map()

  override updated(changed: PropertyValues) {
    super.updated(changed)
    if (changed.has('basePath') && this.basePath) {
      setRmaBaseUrl(new URL(this.basePath, location.origin).origin + '/')
      void this.fetchDraftRma().catch(() => undefined)
      void this.fetchReasons().catch(() => undefined)
      void this.fetchConfig().catch(() => undefined)
    }
  }

  private async fetchDraft(): Promise<void> {
    this.draftRma = await executeRmaRequest(new FetchRmaDraft())
  }

  // The draft is created lazily on the first line add, not eagerly on init, so
  // visiting the page never leaves an empty draft behind.
  private async ensureDraft(): Promise<void> {
    if (this.draftRma instanceof RmaDraftDTO) return
    await this.fetchDraftRma()
    if (this.draftRma instanceof RmaDraftDTO) return
    await executeRmaRequest(new CreateRmaDraft())
    await this.fetchDraftRma()
  }

  private async deduplicateRequest<T>(cacheKey: string, requestFn: () => Promise<T>): Promise<T> {
    if (this.requestCache.has(cacheKey)) {
      return this.requestCache.get(cacheKey) as Promise<T>
    }
    const promise = (async () => {
      try {
        return await requestFn()
      } finally {
        this.requestCache.delete(cacheKey)
      }
    })()
    this.requestCache.set(cacheKey, promise as Promise<void>)
    return promise
  }

  public fetchReasons = async (): Promise<void> => {
    return this.deduplicateRequest('fetchReasons', async () => {
      this.reasons = await executeRmaRequest(new GetRmaReasons())
    })
  }

  public fetchConfig = async (): Promise<void> => {
    return this.deduplicateRequest('fetchConfig', async () => {
      this.config = await executeRmaRequest(new GetRmaConfig())
      if (this.config instanceof RmaConfigDTO) {
        this.disableMemo = !this.config.showMemo
        this.disableMemo2 = !this.config.showMemo2
        this.disableMemo3 = !this.config.showMemo3
      }
    })
  }

  public fetchDraftRma = async (): Promise<void> => {
    return this.deduplicateRequest('fetchDraftRma', async () => {
      await this.fetchDraft()
    })
  }

  @provide({ context: deleteRmaContext })
  public deleteRma = async (): Promise<void> => {
    await executeRmaRequest(new DeleteRmaDraft())
    this.draftRma = null
  }

  @provide({ context: createRmaLineContext })
  public createRmaLine = async (payload: unknown): Promise<void> => {
    await this.ensureDraft()
    const rmaId = this.draftRma instanceof RmaDraftDTO ? this.draftRma.id : null
    if (!rmaId) throw new Error('No draft RMA available to add a line to')
    const linePayload = { ...(payload as object), rmaId }
    const draftWasEmpty = this.draftRma instanceof RmaDraftDTO && this.draftRma.lines.length === 0
    try {
      let result = await executeRmaRequest(new CreateRmaLine(linePayload as never))
      if (result instanceof RmaError) {
        // Removing the last line deletes the whole draft, leaving a stale id in
        // the payload. Refresh to a live draft and retry once — but only when the
        // id actually changed, so genuine 400s (duplicate line, quantity) still
        // surface instead of being retried.
        await this.fetchDraftRma()
        const freshRmaId = this.draftRma instanceof RmaDraftDTO ? this.draftRma.id : null
        if (freshRmaId && freshRmaId !== rmaId) {
          result = await executeRmaRequest(new CreateRmaLine({ ...linePayload, rmaId: freshRmaId } as never))
        }
      }
      if (result instanceof RmaError) throw result
    } catch (error) {
      // A draft-create race leaves two draft RMAs for the same party, and trydent
      // then rejects every line write with a server error. Such a draft is always
      // empty (a line cannot be added while duplicated), so on an empty draft
      // delete the drafts (delete removes every draft for the party) and recreate
      // a single one, then retry the line once.
      if (!draftWasEmpty) throw error
      const freshRmaId = await this.resetToSingleDraft()
      if (!freshRmaId) throw error
      const result = await executeRmaRequest(new CreateRmaLine({ ...linePayload, rmaId: freshRmaId } as never))
      if (result instanceof RmaError) throw result
    }
    await this.fetchDraftRma()
  }

  private async resetToSingleDraft(): Promise<string | null> {
    await executeRmaRequest(new DeleteRmaDraft())
    await executeRmaRequest(new CreateRmaDraft())
    await this.fetchDraftRma()
    return this.draftRma instanceof RmaDraftDTO ? this.draftRma.id : null
  }

  @provide({ context: updateRmaLineContext })
  public updateRmaLine = async (payload: unknown): Promise<void> => {
    const result = await executeRmaRequest(new UpdateRmaLine(payload as never))
    if (result instanceof RmaError) throw result
    await this.fetchDraftRma()
  }

  @provide({ context: updateRmaCommentContext })
  public updateRmaComment = async (comment: string | null): Promise<void> => {
    if (!(this.draftRma instanceof RmaDraftDTO)) return
    const result = await executeRmaRequest(new UpdateRmaComment({ rmaId: this.draftRma.id, comment }))
    if (result instanceof RmaError) throw result
    await this.fetchDraftRma()
  }

  @provide({ context: deleteRmaLineContext })
  public deleteRmaLine = async (lineId: string): Promise<void> => {
    await executeRmaRequest(new DeleteRmaLine({ lineId }))
    await this.fetchDraftRma()
  }

  private uploadBlob = async (file: File): Promise<string> => {
    const formData = new FormData()
    formData.append('file', file)
    const response = await fetcher.fetch(new URL(this.uploadUrl, location.origin), { method: 'POST', body: formData })
    if (!response.ok) {
      const errorData = await response.json().catch(() => null) as { message?: string | string[] } | null
      const raw = errorData?.message
      const message = Array.isArray(raw) ? raw.join(' ') : typeof raw === 'string' && raw ? raw : 'Uploading the file failed. Please try again.'
      throw new RmaError(response.status, '', message)
    }
    const data = await response.json().catch(() => ({})) as { identifier?: string }
    const identifier = String(data.identifier ?? '').replace(/\/+$/, '').split('/').pop() ?? ''
    if (!identifier) throw new RmaError(response.status, '', 'Uploading the file failed. Please try again.')
    return identifier
  }

  @provide({ context: uploadRmaLineFileContext })
  public uploadRmaLineFile = async (lineId: string, file: File): Promise<void> => {
    const identifier = await this.uploadBlob(file)
    await executeRmaRequest(new AddRmaLineUpload({ lineId, identifier }))
    await this.fetchDraftRma()
  }

  @provide({ context: removeRmaLineUploadContext })
  public removeRmaLineUpload = async (lineId: string, identifier: string): Promise<void> => {
    await executeRmaRequest(new RemoveRmaLineUpload({ lineId, identifier }))
    await this.fetchDraftRma()
  }

  @provide({ context: uploadTemporaryRmaLineFileContext })
  public uploadTemporaryRmaLineFile = async (file: File): Promise<string> => {
    const identifier = await this.uploadBlob(file)
    await executeRmaRequest(new CreateTemporaryRmaLineUpload({ identifier }))
    return identifier
  }

  @provide({ context: removeTemporaryRmaLineUploadContext })
  public removeTemporaryRmaLineUpload = async (identifier: string): Promise<void> => {
    await executeRmaRequest(new DeleteTemporaryRmaLineUpload({ identifier }))
  }

  @provide({ context: submitRmaContext })
  public submitRma = async (rmaId: string): Promise<void> => {
    const result = await executeRmaRequest(new SubmitRmaDraft({ rmaId }))
    if (result instanceof RmaError) throw result
    await this.fetchDraftRma()
  }

  @provide({ context: fetchOrdersContext })
  public fetchOrders = async (searchTerm: string): Promise<void> => {
    return this.deduplicateRequest(`fetchOrders:${searchTerm}`, async () => {
      const result = await executeRmaRequest(new SearchRmaOrders({ searchTerm, type: 'sale' }))
      if (Array.isArray(result)) this.searchOrders = result as RmaOrderDTO[]
    })
  }

  @provide({ context: fetchDeliveriesContext })
  public fetchDeliveries = async (searchTerm: string): Promise<void> => {
    return this.deduplicateRequest(`fetchDeliveries:${searchTerm}`, async () => {
      const result = await executeRmaRequest(new SearchRmaOrders({ searchTerm, type: 'shipment' }))
      if (Array.isArray(result)) this.searchDeliveries = result as RmaOrderDTO[]
    })
  }

  @provide({ context: fetchProductsOfOrderContext })
  public fetchProductsOfOrder = async (shipmentID: string, saleID: string): Promise<RmaProductDTO[]> => {
    return this.deduplicateRequest(`fetchProductsOfOrder:${shipmentID}:${saleID}`, async () => {
      const result = await executeRmaRequest(new SearchRmaOrders({ searchTerm: '', type: 'products', shipmentID, saleID }))
      return Array.isArray(result) ? result as RmaProductDTO[] : []
    })
  }

  private get allowAllProducts(): boolean {
    return this.config instanceof RmaConfigDTO && this.config.allowAllProducts
  }

  private async ensureConfig(): Promise<void> {
    if (this.config instanceof RmaConfigDTO) return
    await this.fetchConfig()
  }

  @provide({ context: fetchProductSearchContext })
  public fetchProductSearch = async (searchTerm: string): Promise<void> => {
    return this.deduplicateRequest(`fetchProductSearch:${searchTerm}`, async () => {
      await this.ensureConfig()
      const requests: Promise<unknown>[] = [executeRmaRequest(new SearchRmaDeliveredProducts({ searchTerm }))]
      if (this.allowAllProducts) requests.push(executeRmaRequest(new SearchRmaProducts({ searchTerm })))
      const results = await Promise.all(requests)
      this.searchProducts = results.flatMap((result) => (Array.isArray(result) ? result as RmaProductDTO[] : []))
    })
  }

  @provide({ context: fetchLatestOrdersContext })
  public fetchLatestOrders = async (limit = 5): Promise<void> => {
    return this.deduplicateRequest(`fetchLatestOrders:${limit}`, async () => {
      const result = await executeRmaRequest(new SearchRmaOrders({ searchTerm: '*', type: 'sale', limit }))
      if (Array.isArray(result)) this.latestOrders = result as RmaOrderDTO[]
    })
  }
}
